import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

export type FieldSelectOption = {
  value: string;
  label: string;
};

type FieldSelectProps = {
  label: string;
  value: string;
  options: FieldSelectOption[];
  onChange: (value: string) => void;
  className?: string;
  labelClassName?: string;
  uppercaseLabel?: boolean;
};

export const FieldSelect = ({
  label,
  value,
  options,
  onChange,
  className = "",
  labelClassName = "",
  uppercaseLabel,
}: FieldSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const matchedOption = options.find((option) => option.value === value);
  const displayLabel = matchedOption?.label ?? value ?? options[0]?.label ?? "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    closeMenu();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      closeMenu();
      return;
    }
    if (
      event.key === "ArrowDown" ||
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div ref={wrapperRef} className={`flex flex-col gap-2 text-sm ${className}`}>
      <span
        className={`text-gray-300 ${
          uppercaseLabel ? "tracking-wide" : ""
        } ${labelClassName}`.trim()}
      >
        {label}
      </span>
      <div className="relative">
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-md border border-border bg-gray-900 px-3 py-2 text-left text-sm focus:border-primary focus:outline-none"
          onClick={toggleOpen}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="text-gray-100">{displayLabel}</span>
          <span
            className={`ml-2 text-gray-400 transition-transform duration-150 ${
              isOpen ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
        {isOpen && (
          <div className="absolute left-0 right-0 z-30 mt-1 rounded-md border border-border bg-gray-900 shadow-2xl">
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1">
              {options.map((option, index) => {
                const isActive = option.value === value;
                return (
                  <li key={`${option.value || "default"}-${index}`}>
                    <button
                      type="button"
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-800 focus:bg-gray-800 ${
                        isActive ? "text-primary" : "text-gray-100"
                      }`}
                      onClick={() => handleSelect(option.value)}
                      role="option"
                      aria-selected={isActive}
                    >
                      {option.label}
                      {isActive && <span className="text-primary">•</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
